/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColDef, GridOptions, GridReadyEvent } from '@ag-grid-community/core'
import { Component, Input, OnDestroy } from '@angular/core'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  heliosDefaultColDef,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { AgGridApi } from '@northfork/ag-grid'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { PricingKey } from '../../panel-key'
import { PriceDataResponse } from '../models/price-data'

const PricingMainGridKey = `${PricingKey}-MainGrid`

@Component({
  selector: 'pricing-data-by-source-table',
  templateUrl: './pricing-data-by-source-table.component.html',
})
export class PricingDataBySourceTableComponent implements OnDestroy {
  columnField: string[] = []
  columnFieldMap: Map<string, string> = new Map([
    ['Security ID', 'securityId'],
    ['Date', 'pricingDate'],
    ['Account Price', 'price'],
    ['Account Yield', 'yield'],
    ['Account Duration', 'duration'],
    ['Prezo Chosen Source', 'chosenPriceSource'],
  ])
  details: any[] = []
  agGridApi?: AgGridApi
  _priceData: PriceDataResponse[] = []
  _pricingSource: Set<string> = new Set()
  @Input() securityIds: Set<number> = new Set()
  @Input() lastPricingRunDate = ''
  @Input() lastPrice = ''
  @Input() set pricingSource(pricingSource: Set<string>) {
    this._pricingSource = pricingSource
  }
  @Input() set priceData(priceData: PriceDataResponse[]) {
    this._priceData = priceData
    this.updateRowDetails()
  }

  private readonly destroySubject$ = new Subject<void>()

  constructor(
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  private updateRowDetails() {
    this.columnField = [
      'Security ID',
      'Date',
      'Account Yield',
      'Account Duration',
      'Prezo Chosen Source',
      'Account Price',
    ]
    if (this._pricingSource) {
      this._pricingSource.forEach(source => {
        this.columnField.push(source)
      })
      this.agGridApi?.api.setColumnDefs(this.fileDetailsColDefs())
    }
    if (this._priceData) {
      const rowDetail: any[] = []
      this._priceData.forEach(data => {
        const priceDataMap = new Map(Object.entries(data))

        if (priceDataMap.get('comparisonPrices') != null) {
          const comparisonPrices = Array.from(
            priceDataMap.get('comparisonPrices'),
          )
          comparisonPrices.forEach((comparisonPrice: any) => {
            const comparisonPriceMap = new Map(Object.entries(comparisonPrice))
            comparisonPriceMap.forEach((value, key) => {
              priceDataMap.set(key, value)
              this.columnFieldMap.set(key, key)
            })
          })
        }
        const obj = this.columnField.reduce((accumulator, element) => {
          return {
            ...accumulator,
            [element]: priceDataMap.get(this.columnFieldMap.get(element)!),
          }
        }, {})
        rowDetail.push(obj)
      })
      this.details = rowDetail
      this.agGridApi?.api.setRowData(this.details)
    }
    if (this.securityIds) {
      this.updateSecurity()
    }
  }

  fileDetailsColDefs(): ColDef[] {
    const colDef: any[] = []
    if (this.columnField) {
      this.columnField.forEach(column => {
        colDef.push({ field: column })
      })
    }
    return colDef
  }
  securityIdsWithoutData: number[] = []

  updateSecurity() {
    const secIdsWithData: Set<number> = new Set()
    for (const value of this._priceData) {
      secIdsWithData.add(value.securityId)
    }
    this.securityIdsWithoutData = Array.from(this.securityIds).filter(
      secId => !secIdsWithData.has(secId),
    )
  }

  gridOptions: GridOptions = {
    onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
    ...heliosSizingAdjustments,
    defaultColDef: {
      ...heliosDefaultColDef,
      menuTabs: [],
      sortable: true,
    },
    columnDefs: this.fileDetailsColDefs(),
    sideBar: false,
    onFirstDataRendered: (params: any) => {
      params.api.sizeColumnsToFit()
    },
    onRowDataChanged: (params: any) => {
      params.api.sizeColumnsToFit()
    },
  }

  private onGridReady(event: GridReadyEvent) {
    const agGridApi: AgGridApi = { api: event.api, columnApi: event.columnApi }
    this.agGridApi = agGridApi
    this.updateRowDetails()

    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )
    this.savedViewService.manageSavedView(
      event,
      PricingMainGridKey,
      this.destroySubject$,
    )
    enableHeliosCopyBehavior(event.api)
  }
}
